<template>
  <div id="survey-results" class="pa-4">
    <div class="banner-header">
      <div>
        <v-btn
          text
          color="white"
          @click="$router.push('/survey/list')"
          class="mb-2 pl-0"
        >
          <v-icon left>mdi-arrow-left</v-icon> Back to Surveys
        </v-btn>
        <h1 class="page-title">Results</h1>
        <p class="mt-1 mb-0 banner-subtitle">
          {{ TotalResponses }} response(s) collected
        </p>
      </div>
    </div>

    <div class="d-flex justify-content-center mb-6" v-if="LoadingFlag">
      <b-spinner variant="primary" type="grow" label="Loading..."></b-spinner>
    </div>

    <v-tabs v-model="tab" background-color="transparent" v-if="!LoadingFlag">
      <v-tab>Responses</v-tab>
      <v-tab>Summary</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" v-if="!LoadingFlag">
      <!-- Raw responses -->
      <v-tab-item>
        <v-card class="result-card elevation-4 rounded-xl border-0 mt-4">
          <v-card-text class="pa-2 px-md-4">
            <div class="d-flex justify-end pb-2" v-if="rows.length > 0">
              <v-btn
                color="#3A67B1"
                class="rounded-lg text-white"
                elevation="0"
                @click="
                  generateExcel(
                    rows,
                    excelFields,
                    'survey-responses.xlsx',
                    'Responses'
                  )
                "
              >
                <v-icon left>mdi-download</v-icon> Download
              </v-btn>
            </div>
            <div class="text-center py-10" v-if="rows.length == 0">
              <h4 class="text-muted font-weight-bold">No responses yet.</h4>
            </div>
            <div v-else style="overflow-x: auto">
              <v-data-table
                :headers="tableColumns1"
                :items="rows"
                :items-per-page="25"
              >
                <template
                  v-for="col in fileColumns"
                  v-slot:[`item.${col}`]="{ item }"
                >
                  <a
                    v-if="item[col]"
                    :key="col"
                    :href="fileBaseUrl + item[col]"
                    target="_blank"
                    rel="noopener"
                    >{{ item[col] }}</a
                  >
                  <span v-else :key="col">-</span>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Per-question summary -->
      <v-tab-item>
        <div class="mt-4">
          <v-card
            v-for="q in summaryQuestions"
            :key="q.QuestionId"
            class="result-card elevation-4 rounded-xl border-0 mb-4"
          >
            <v-card-text class="pa-6">
              <h5 class="font-weight-bold mb-3">{{ q.QuestionText }}</h5>

              <div v-if="q.Counts">
                <div
                  v-if="Object.keys(q.Counts).length == 0"
                  class="text-muted"
                >
                  No answers yet.
                </div>
                <div
                  v-for="(count, option) in q.Counts"
                  :key="option"
                  class="mb-2"
                >
                  <div
                    class="d-flex justify-space-between"
                    style="font-size: 13px"
                  >
                    <span>{{ option }}</span>
                    <span class="font-weight-bold">{{ count }}</span>
                  </div>
                  <v-progress-linear
                    :value="percentOf(count, q.Counts)"
                    color="#8950FC"
                    height="10"
                    rounded
                  ></v-progress-linear>
                </div>
              </div>

              <div v-else>
                <div v-if="q.Answers.length == 0" class="text-muted">
                  No answers yet.
                </div>
                <ul v-else>
                  <li v-for="(ans, idx) in q.Answers" :key="idx">
                    <a
                      v-if="isFileType(q.QuestionType)"
                      :href="fileBaseUrl + ans"
                      target="_blank"
                      rel="noopener"
                      >{{ ans }}</a
                    >
                    <span v-else>{{ ans }}</span>
                  </li>
                </ul>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  mixins: [common],
  data() {
    return {
      LoadingFlag: true,
      SurveyId: this.$route.params.id,
      tab: 0,

      rows: [],
      tableColumns1: [],
      excelFields: {},
      fileQuestionIds: [],
      fileBaseUrl: "",

      TotalResponses: 0,
      summaryQuestions: [],
    };
  },
  computed: {
    fileColumns() {
      var ids = this.fileQuestionIds;
      return ids.map(function (id) {
        return "Q" + id;
      });
    },
  },
  methods: {
    isFileType(questionType) {
      return questionType == 5 || questionType == 12;
    },
    percentOf(count, counts) {
      var total = Object.values(counts).reduce(function (sum, v) {
        return sum + v;
      }, 0);
      return total > 0 ? (count / total) * 100 : 0;
    },
    getResults() {
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      const thisIns = this;

      this.$http({
        url: server_url + "api/survey/results",
        method: "POST",
        data: { SurveyId: this.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      }).then(function (response) {
        var records = response.data.records;
        thisIns.tableColumns1 = records.TableHeader || [];
        thisIns.rows = records.TableData || [];
        thisIns.fileQuestionIds = records.FileQuestionIds || [];
        thisIns.fileBaseUrl = records.FileBaseUrl || "";

        var fields = {};
        (records.TableHeader || []).forEach(function (h) {
          fields[h.text] = h.value;
        });
        thisIns.excelFields = fields;
      });

      this.$http({
        url: server_url + "api/survey/results-summary",
        method: "POST",
        data: { SurveyId: this.SurveyId },
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          thisIns.LoadingFlag = false;
          var records = response.data.records;
          thisIns.TotalResponses = records.TotalResponses;
          thisIns.summaryQuestions = records.Questions || [];
        })
        .catch(function () {
          thisIns.LoadingFlag = false;
        });
    },
  },
  beforeMount() {
    this.getResults();
  },
};
</script>

<style lang="scss" scoped>
#survey-results {
  .banner-header {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%);
    border-radius: 20px;
    padding: 1.5rem 2.5rem;
    margin-bottom: 24px;
  }
  .page-title {
    color: #ffffff;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
  }
  .banner-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
  }
  .result-card {
    border-radius: 20px !important;
    box-shadow: 0 10px 40px rgba(10, 15, 41, 0.05) !important;
  }
}
</style>
